import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { LobBankAccount } from '../../../models/entities/lob-bank-account';
import { distinctUntilChanged } from 'rxjs';
import { ControlMessagesComponent } from '../control-messages/control-messages.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'app-check-number-input',
    templateUrl: './check-number-input.component.html',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ControlMessagesComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CheckNumberInputComponent implements OnInit, OnChanges {
    @Input() formGroup: FormGroup;
    @Input() controlName: string = 'checkNumber';
    @Input() bankAccountControlName: string = 'bankAccountId';
    @Input() bankAccounts: LobBankAccount[] = [];
    @Input() placeholder: string = 'Check Number';
    @Input() checkNumberOverride?: string | number;

    private hasUserEdited: boolean = false;

    get control(): FormControl {
        return this.formGroup?.get(this.controlName) as FormControl;
    }

    ngOnInit() {
        this.setupBankAccountWatcher();
        this.setCheckNumber();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.checkNumberOverride && !changes.checkNumberOverride.firstChange) {
            this.setCheckNumber();
        } else if (changes.bankAccounts && !changes.bankAccounts.firstChange) {
            this.setCheckNumber();
        }
    }

    limitToNumbers() {
        if (!this.control?.value) {
            return;
        }

        this.hasUserEdited = true;

        const currentValue = this.control.value.toString();
        const cleanedValue = currentValue.replace(/[^\d]/g, '');

        if (currentValue !== cleanedValue) {
            this.control.setValue(cleanedValue, { emitEvent: false });
        }
    }

    private setupBankAccountWatcher() {
        const bankAccountControl = this.formGroup?.get(this.bankAccountControlName);
        if (!bankAccountControl) {
            return;
        }

        let previousAccountId = bankAccountControl.value;

        bankAccountControl.valueChanges
                .pipe(distinctUntilChanged(), untilDestroyed(this))
                .subscribe(accountId => {
                    if (accountId !== previousAccountId) {
                        previousAccountId = accountId;
                        this.hasUserEdited = false;
                        if (this.checkNumberOverride !== undefined && this.checkNumberOverride !== null) {
                            this.control?.setValue(this.checkNumberOverride);
                            return;
                        }
                        if (accountId) {
                            const selectedAccount = this.bankAccounts.find(b => b.id === accountId);
                            if (selectedAccount?.verified && selectedAccount.nextCheckNumber) {
                                this.control?.setValue(selectedAccount.nextCheckNumber);
                            }
                        }
                    }
                });
    }

    private setCheckNumber() {
        if (this.hasUserEdited) {
            return;
        }
        if (this.checkNumberOverride !== undefined && this.checkNumberOverride !== null) {
            this.control?.setValue(this.checkNumberOverride);
            return;
        }
        if (this.bankAccounts?.length === 1 && this.formGroup?.get(this.bankAccountControlName)?.value) {
            const singleAccount = this.bankAccounts[0];
            if (singleAccount.verified && singleAccount.nextCheckNumber) {
                this.control?.setValue(singleAccount.nextCheckNumber);
            }
        }
    }
}
